import React, { useCallback, useMemo, useState } from "react";

import { useSelector } from "react-redux";
import { useDebounce } from "@react-hook/debounce";

import AgGridTable from "components/AgGrid/AgGridContainer";
import TableWrapper from "components/AgGrid/TableWrapper";
import { SlimSecondaryButton } from "components/Button";
import { SecondaryButton } from "components/Form";
import { Column, Row } from "components/Layout";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { SmallText } from "components/Text";

import { AgGridTables } from "constants/aggrid";
import {
  SideLoadedBusinessAutoSizedColumnIds,
  SideLoadedBusinessColumnDefs,
} from "constants/columnDefinitions/sideLoadedBusiness";

import { EMPTY_ARRAY } from "lib";
import {
  isHiddenSideLoadedBusiness,
  pickSideLoadedSearchResults,
  searchSideLoadedBusinessesWithMatches,
  selectSideLoadedBusinessesGridList,
} from "selectors";
import SearchInput from "components/SearchInput";

import styled from "styled-components/macro";

import {
  getSideLoadedBusinessRowId,
  useSideLoadedBusinessGridContext,
} from "./useSideLoadedBusinessGridContext";

interface SideLoadedBusinessTableProps {
  initialSearchTerm?: string;
  onClose: () => void;
  onSelect: (recordId: string) => void;
}

export const SideLoadedBusinessTableWrapper = styled(TableWrapper)(
  ({ minHeight = 400 }) => `
  min-height: ${minHeight}px;
`,
);
const SideLoadedBusinessTable = ({
  initialSearchTerm = "",
  onClose,
  onSelect,
}: SideLoadedBusinessTableProps): React.JSX.Element => {
  // Use our Fuse search results, rather than the embedded AgGrid one in order to give
  // consistent results.
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(
    searchTerm,
    300,
  );
  React.useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [setSearchTerm, debouncedSearchTerm]);
  React.useEffect(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [setDebouncedSearchTerm, searchTerm]);

  const [showHidden, setShowHidden] = useState(false);

  // Always fetch hidden records too, so toggling doesn't have to re-query -
  // and so hiddenCount below can say how many of *these* results are hidden,
  // rather than how many are hidden across the whole (unsearched) data set.
  const sideLoadStrictResults = useSelector(
    searchSideLoadedBusinessesWithMatches(debouncedSearchTerm, true, true),
  );
  const sideLoadLiberalResults = useSelector(
    searchSideLoadedBusinessesWithMatches(debouncedSearchTerm, false, true),
  );
  const sideLoadResults = pickSideLoadedSearchResults(
    sideLoadStrictResults,
    sideLoadLiberalResults,
    showHidden,
  );

  const sideLoadIds = useMemo(
    () =>
      debouncedSearchTerm
        ? sideLoadResults
            .filter(result => showHidden || !isHiddenSideLoadedBusiness(result))
            .map(result => result.id)
        : null,
    [sideLoadResults, debouncedSearchTerm, showHidden],
  );

  const matchedFieldsById = useMemo(
    () =>
      new Map(sideLoadResults.map(result => [result.id, result.matchedFields])),
    [sideLoadResults],
  );

  // Rows come from the same selector the Manage grid uses, so the shared
  // column definitions get the datasetLabel and validationIssues they read,
  // rather than this grid recomputing them.
  const gridList = useSelector(selectSideLoadedBusinessesGridList);
  const gridListById = useMemo(
    () => new Map(gridList.map(business => [business.id, business])),
    [gridList],
  );

  // With a search term active, count hidden records among the matches - the
  // button should promise exactly how many rows toggling it on will add, not
  // the total hidden count across records the search has already excluded.
  const hiddenCount = useMemo(() => {
    if (debouncedSearchTerm) {
      return sideLoadResults.filter(isHiddenSideLoadedBusiness).length;
    }
    return gridList.filter(isHiddenSideLoadedBusiness).length;
  }, [debouncedSearchTerm, sideLoadResults, gridList]);

  // Show all results when there is no search term.
  const rowData = useMemo(() => {
    if (!sideLoadIds) {
      return gridList
        .filter(business => showHidden || !isHiddenSideLoadedBusiness(business))
        .map(business => ({
          ...business,
          matchedFields: EMPTY_ARRAY,
        }));
    }
    // keep ordering of Fuse results for initial order in Ag-Grid
    return sideLoadIds
      .map(id => gridListById.get(id))
      .filter(Boolean)
      .map(business => ({
        ...business,
        matchedFields: matchedFieldsById.get(business.id) ?? EMPTY_ARRAY,
      }));
  }, [showHidden, gridList, gridListById, sideLoadIds, matchedFieldsById]);

  const handleSelectAndCompare = useCallback(
    (selectedRecordId: string) => {
      if (selectedRecordId) {
        onSelect(selectedRecordId);
      }
    },
    [onSelect],
  );

  const context = useSideLoadedBusinessGridContext(handleSelectAndCompare);

  const standardPreamble =
    "Not finding what you're looking for? You can edit your search or fix spelling to see more";
  const noSearchTermsPreamble = "Enter search text to search suggestions";
  const noResultsPreamble =
    "No suggestions found. You can edit the search to fix spelling or include fewer words.";
  const noExactResultsPreamble =
    "No exact matches found. Searching for any of the entered terms instead. You can try fixing spelling, removing generic terms or including fewer terms to see exact matches.";

  let preamble = standardPreamble;
  if (debouncedSearchTerm.length === 0) {
    preamble = noSearchTermsPreamble;
  } else if (rowData.length === 0) {
    preamble = noResultsPreamble;
  } else if (sideLoadResults !== sideLoadStrictResults) {
    preamble = noExactResultsPreamble;
  }

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle onClose={onClose}>Data Suggestions</DialogTitle>
      <DialogContent dividers>
        <Column full>
          <Row paddingVertical={1}>
            <SmallText>
              {preamble} Suggestions are sourced from your supplied Legacy
              Business data.
            </SmallText>
          </Row>
          <Row fullWidth alignCenter gridGap={1}>
            <SearchInput
              value={searchTerm}
              handleChange={setSearchTerm}
              style={{ marginLeft: 0 }}
            />
            {(hiddenCount > 0 || showHidden) && (
              <SlimSecondaryButton
                onClick={() => setShowHidden(!showHidden)}
                type="button"
                title="Hidden suggestions are those already used or ignored"
              >
                {showHidden ? "Hide hidden" : `Show ${hiddenCount} hidden`}
              </SlimSecondaryButton>
            )}
          </Row>
          <AgGridTable
            columnDefs={SideLoadedBusinessColumnDefs}
            autoSizeColumnIds={SideLoadedBusinessAutoSizedColumnIds}
            rowData={rowData}
            getRowId={getSideLoadedBusinessRowId}
            hasExportCsvPermissions={false}
            tableName={AgGridTables.SIDELOADED_BUSINESSES}
            WrapperComponent={SideLoadedBusinessTableWrapper}
            showGlobalSearchFilter={false}
            hideSavedViews
            context={context}
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
      </DialogActions>
    </ZoomyDialog>
  );
};

export default SideLoadedBusinessTable;
